import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";

type NoteType = "Note" | "Follow-up" | "Meeting" | "Reminder";

interface Props {
  onClose: () => void;
}

export default function QuickNoteModal({ onClose }: Props) {
  const { showToast } = useToast();
  const [leadSearch, setLeadSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLead, setSelectedLead] = useState<{ id?: string | number; name: string; phone?: string; company?: string } | null>(null);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState<NoteType>("Note");
  const [leadsList, setLeadsList] = useState<{ id: string | number; name: string; phone?: string; company?: string }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchApi<any>("/leads", undefined, [])
      .then((res) => {
        const items = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.items)
          ? res.items
          : [];
        setLeadsList(
          items.map((l: any) => ({
            id: l.id,
            name: l.name || "Lead",
            phone: l.phone || "",
            company: l.company || "",
          }))
        );
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const filteredLeads = useMemo(() => {
    if (!leadSearch.trim()) return leadsList.slice(0, 8);
    return leadsList
      .filter(
        (l) =>
          l.name.toLowerCase().includes(leadSearch.toLowerCase()) ||
          (l.phone && l.phone.includes(leadSearch)) ||
          (l.company && l.company.toLowerCase().includes(leadSearch.toLowerCase()))
      )
      .slice(0, 8);
  }, [leadsList, leadSearch]);

  const handleSelectLead = (lead: { id?: string | number; name: string; phone?: string; company?: string }) => {
    setSelectedLead(lead);
    setLeadSearch(lead.name);
    setShowDropdown(false);
  };

  const handleSave = async () => {
    if (!selectedLead) {
      showToast("Please select a lead", "error");
      return;
    }
    if (!noteText.trim()) {
      showToast("Please write a note", "error");
      return;
    }

    setSaving(true);
    try {
      await fetchApi("/activities", {
        method: "POST",
        body: JSON.stringify({
          type: "note",
          title: `${noteType} added`,
          description: noteText.trim(),
          lead_name: selectedLead.name,
          lead_id: selectedLead.id ? String(selectedLead.id) : null,
          user_name: "Admin",
          detail: `[${noteType}] ${noteText.trim()}`,
        }),
      });

      if (selectedLead.id) {
        await fetchApi(`/leads/${selectedLead.id}/activities`, {
          method: "POST",
          body: JSON.stringify({
            type: noteType,
            description: noteText.trim(),
            performedBy: "Admin",
          }),
        }).catch(() => {});
      }

      showToast(`Note saved on ${selectedLead.name}`, "success");
      onClose();
    } catch (err: any) {
      showToast(err.message || "Failed to save note", "error");
    } finally {
      setSaving(false);
    }
  };

  const noteTypes: NoteType[] = ["Note", "Follow-up", "Meeting", "Reminder"];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl w-full max-w-md shadow-lg animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Quick Note</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Lead search */}
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Lead</label>
            {selectedLead ? (
              <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-xs font-bold text-indigo-600">
                    {selectedLead.name.split(" ").map((n) => n[0]).join("")}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900">{selectedLead.name}</p>
                <button
                  onClick={() => { setSelectedLead(null); setLeadSearch(""); }}
                  className="ml-auto text-gray-400 hover:text-red-500"
                >
                  <i className="ri-close-circle-line" />
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={leadSearch}
                  onChange={(e) => { setLeadSearch(e.target.value); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Search lead by name or phone..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
                {showDropdown && filteredLeads.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                    {filteredLeads.slice(0, 8).map((l) => (
                      <button
                        key={l.id}
                        onClick={() => handleSelectLead(l)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                      >
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-indigo-600">
                            {l.name.split(" ").map((n) => n[0]).join("")}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm text-gray-900">{l.name}</p>
                          <p className="text-xs text-gray-400">{l.company} · {l.phone}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Note type pills */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Type</label>
            <div className="flex items-center gap-2">
              {noteTypes.map((t) => (
                <button
                  key={t}
                  onClick={() => setNoteType(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    noteType === t
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Note textarea */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Note</label>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Write your note here..."
              className="w-full border border-gray-200 rounded-lg p-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-300 resize-none"
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{noteText.length}/500</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
          >
            Save Note
          </button>
        </div>
      </div>
    </div>
  );
}